<template>
  <div id="projectList">
    <!-- 顶部返回条 -->
    <div class="topbar" :class="{ 'is-scrolled': scrolled }">
      <div class="container topbar-inner">
        <router-link to="/" class="back-link">
          <span class="back-arrow">←</span> 返回首页
        </router-link>
        <span class="topbar-title">{{ category.name }}</span>
      </div>
    </div>

    <!-- Hero -->
    <section class="ph-hero">
      <div class="container">
        <div class="ph-hero-inner reveal-stagger">
          <div class="ph-kicker">PROJECTS · {{ category.name.toUpperCase() }}</div>
          <h1 class="ph-title">{{ category.name }}案例集</h1>
          <p class="ph-desc">{{ category.desc }}</p>
          <div class="ph-stat">
            <span class="ph-stat-num">500+</span>
            <span class="ph-stat-label">个落地项目</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 分类切换 -->
    <section class="ph-tabs">
      <div class="container">
        <div class="ph-tabs-inner">
          <router-link v-for="c in categories" :key="c.slug" :to="'/projects/' + c.slug" class="ph-tab"
            :class="{ active: c.slug === slug }">
            <span class="ph-tab-text">{{ c.name }}</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 项目网格 -->
    <section class="ph-grid-section">
      <div class="container">
        <div class="ph-grid" v-if="list.length">
          <article v-for="(p, i) in list" :key="i" class="ph-card reveal"
            :style="{ transitionDelay: (i % 3) * 0.08 + 's' }">
            <div class="ph-cover">
              <img :src="p.img" :alt="p.name" mode="aspectFill" />
              <div class="ph-cover-mask"></div>
              <div class="ph-tags">
                <span v-for="(t, ti) in p.tags" :key="ti">{{ t }}</span>
              </div>
              <div class="ph-year">{{ p.year }}</div>
            </div>
            <div class="ph-body">
              <div class="ph-no">{{ String(i + 1).padStart(2, "0") }}</div>
              <h3 class="ph-name">{{ p.name }}</h3>
              <div class="ph-meta">
                <span class="ph-meta-k">类型</span>
                <span class="ph-meta-v">{{ p.type }}</span>
              </div>
              <p class="ph-desc">{{ p.desc }}</p>
              <div class="ph-foot">
                <div class="ph-client">
                  <span class="ph-client-k">客户</span>
                  <span class="ph-client-v">{{ p.client }}</span>
                </div>
                <span class="ph-cta" @click.stop="openDetail(p)">查看详情 →</span>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="ph-empty">该分类下暂无项目</div>
      </div>
    </section>

    <!-- 底部 -->
    <footer class="ph-footer">
      <div class="container">
        <router-link to="/" class="ph-footer-back">返回首页</router-link>
        <a href="https://beian.miit.gov.cn" class="ph-icp">
          粤ICP备18089087号 · Copyright © Guangdong gusher.tech Rights Reserved
        </a>
      </div>
    </footer>

    <!-- 项目详情弹窗 -->
    <transition name="ph-fade">
      <div v-if="activeProject" class="ph-modal" @click.self="closeDetail">
        <div class="ph-modal-box">
          <button class="ph-modal-close" @click="closeDetail" aria-label="关闭">
            ×
          </button>
          <div class="ph-modal-head">
            <div class="ph-modal-kicker">
              {{ activeProject.type }} · {{ activeProject.year }}
            </div>
            <h3 class="ph-modal-title">{{ activeProject.name }}</h3>
            <p class="ph-modal-desc">{{ activeProject.detail }}</p>
          </div>
          <div class="ph-modal-highlights" v-if="activeProject.highlights && activeProject.highlights.length">
            <div v-for="(h, hi) in activeProject.highlights" :key="hi" class="ph-hl">
              <span class="ph-hl-dot"></span>{{ h }}
            </div>
          </div>
          <div class="ph-modal-grid" v-if="activeProject.gallery && activeProject.gallery.length">
            <div v-for="(g, gi) in activeProject.gallery" :key="gi" class="ph-modal-img" @click="previewImg(g)">
              <img :src="g" :alt="activeProject.name + ' 截图 ' + (gi + 1)" loading="lazy" />
              <span class="ph-modal-img-tip">{{ gi + 1 }} / {{ activeProject.gallery.length }}</span>
            </div>
          </div>
          <div class="ph-modal-tip">点击图片可放大查看</div>
        </div>
      </div>
    </transition>

    <!-- 大图预览 -->
    <transition name="ph-fade">
      <div v-if="activeImg" class="ph-imgview" @click="closeImg">
        <img :src="activeImg" alt="预览大图" />
        <button class="ph-imgview-close" @click.stop="closeImg" aria-label="关闭">
          ×
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { setSeo } from "@/utils/seo";

const img = (prompt, size = "landscape_4_3") =>
  "https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=" +
  encodeURIComponent(prompt) +
  "&image_size=" +
  size;

export default {
  name: "homePage",
  data() {
    return {
      scrolled: false,
      activeProject: null,
      activeImg: "",
      categories: [
        {
          slug: "custom",
          name: "定制化软件",
          desc: "按业务流程量身设计，从需求到交付全链路工程化。",
        },
        {
          slug: "website",
          name: "企业官网",
          desc: "品牌展示与对外门户，兼顾形象与信息架构，兼顾多端访问与搜索引擎可见性。",
        },
        {
          slug: "miniapp",
          name: "微信小程序",
          desc: "轻量移动端应用，触达现场人员与公众用户，即用即走。",
        },
        {
          slug: "industrial",
          name: "工业化软件",
          desc: "面向工业现场的监控、调度与设备集成系统，稳定可靠。",
        },
        {
          slug: "app",
          name: "应用程序",
          desc: "跨平台应用交付，覆盖桌面、移动与现场终端。",
        },
      ],
      projectMap: {
        website: [
          {
            name: '"互联网+智慧监管"',
            type: "政务官网",
            desc: "识别农贸市场占道经营、违章停车等事件，并通过派单管理方式下达各站点负责人处置。",
            tags: ["智慧监管", "事件识别", "派单管理"],
            client: "市场监管局",
            year: "2023",
            img: require("../assets/jinpin/one.png"),
            gallery: [
              require("../assets/jinpin/one.png"),
              require("../assets/jinpin/tow.png"),
              require("../assets/jinpin/thress.png"),
            ],
            detail:
              '"互联网+智慧监管"平台整合市场内外现有监控资源，通过视频转码、视频分析技术，自动识别农贸市场占道经营、违章停车等违规事件，识别结果以智能派单形式直达各站点负责人，实现发现、处置、复核全流程闭环，大幅提升基层监管效率。',
            highlights: [
              "视频转码与AI识别：占道经营 / 违章停车 自动识别",
              "智能派单闭环：识别→派单→处置→复核全流程留痕",
              "GIS事件一张图：实时定位违规点位与处置进度",
              "多端联动：监管大屏 + 站点负责人APP协同",
            ],
          },
        ],
        miniapp: [
          {
            name: "低碳游",
            type: "微信小程序",
            desc: "以低碳环保为核心理念的微信小程序，围绕打卡、积分、商城、商家、回收、学习场景，形成「行为→积分→权益」闭环。",
            tags: ["低碳", "积分", "打卡", "团长分销"],
            client: "中国联通",
            year: "2026",
            img: require("../assets/wxxcx/dt/dtsy.jpg"),
            gallery: [
              require("../assets/wxxcx/dt/dtsy.jpg"),
              require("../assets/wxxcx/dt/dtdt.jpg"),
              require("../assets/wxxcx/dt/dthd.jpg"),
              require("../assets/wxxcx/dt/dtme.jpg"),
              require("../assets/wxxcx/dt/dtsp.jpg"),
              require("../assets/wxxcx/dt/dtxx.jpg"),
              require("../assets/wxxcx/dt/dtxxsp.jpg"),
            ],
            detail:
              "「低碳游」是一款以低碳环保为核心理念的微信小程序，围绕用户日常低碳行为（到店打卡、拍照记录、旧物回收、学习低碳知识等）发放积分，积分可在商城兑换商品/券码，形成「行为 → 积分 → 权益」完整闭环。平台同时连接 C 端用户与 B 端商家，并提供团长分销能力。",
            highlights: [
              "行为激励闭环：低碳行为 → 积分 → 权益兑换",
              "地图化低碳打卡：LBS 地图标记、分类筛选、拍照/扫码打卡",
              "多角色一体化：普通用户 / 商家 / 团长 动态切换界面",
              "团长分销体系：申请团长、商品分销、分享海报、团队统计",
              "安全细节：首页截屏防护、到期全局提醒",
              "工程化：TDesign 组件库、按需加载、统一接口封装",
            ],
          },
        ],
        industrial: [
          {
            name: "海上监测平台",
            type: "海洋物联",
            desc: "手持设备软件定位查看航道，实时检测巡查船只路径，发现问题智能记录坐标并上传平台。",
            tags: ["海洋物联", "航道巡检", "坐标回传"],
            client: "海洋监测站",
            year: "2022",
            img: require("../assets/nanao/one.png"),
            gallery: [
              require("../assets/nanao/one.png"),
              require("../assets/nanao/tow.png"),
              require("../assets/nanao/thress.png"),
            ],
            detail:
              "海上监测平台整合海洋监测设备与手持软件，实现航道巡查全流程数字化。巡查人员可通过手持设备软件定位查看行驶中的航道，实时检测巡查船只路径并记录，现场发现问题时监管人员记录拍照，系统将智能记录坐标并上传至平台，构建可追溯的海上巡查数据链路。",
            highlights: [
              "航道定位：实时查看航道与船只路径",
              "路径留痕：巡查航迹自动记录，可回放与统计",
              "现场记录：问题拍照 + 智能坐标回传平台",
              "数据汇聚：平台端多源数据一张图统一展示",
            ],
          },
          {
            name: "养殖区智慧平台",
            type: "农业物联",
            desc: "养殖区水质、气象、投喂设备物联接入与远程调度，物料分配-归纳-匹配-出库-包装全流程。",
            tags: ["物联", "远程调度", "水质监测"],
            client: "ABB",
            year: "2022",
            img: require("../assets/one.png"),
            gallery: [
              require("../assets/one.png"),
            ],
            detail:
              "养殖区智慧平台面向水产养殖场景，物联接入水质、气象、投喂设备，实现远程调度与数字化管理。物料从分配、归纳、匹配到出库、包装全流程跟踪，降低人工巡检成本，提升养殖效率与管理精度。",
            highlights: [
              "设备物联：水质 / 气象 / 投喂设备接入",
              "远程调度：现场设备远程控制与策略下发",
              "物料追溯：分配-归纳-匹配-出库-包装全流程",
              "预警告警：阈值自动告警与处置建议",
            ],
          },
          {
            name: "智慧路灯管理平台",
            type: "城市物联",
            desc: "接入深圳城市部分道路路灯，实现集中监控、远程开关、能耗统计与检修工单一体化管理。",
            tags: ["路灯", "远程控制", "工单"],
            client: "ABB",
            year: "2022",
            img: require("../assets/one.png"),
            gallery: [
              require("../assets/one.png"),
            ],
            detail:
              "智慧路灯管理平台接入深圳城市部分道路路灯，实现集成控制并进行检修管理，覆盖远程开关灯、亮度调节、运行状态监控、能耗统计、检修工单派发等一体化能力，提升市政路灯运维效率。",
            highlights: [
              "集中控制：远程开关、亮度策略统一管理",
              "实时监控：路灯运行状态与故障告警",
              "能耗统计：分路 / 分区能耗看板与对比",
              "检修工单：故障自动派发维修人员",
            ],
          },
          {
            name: "地平线",
            type: "智慧系统",
            desc: "智驾场景视频展示开发，展示驾驶场景下的视频内容。",
            tags: ["视频展示", "视频", "安全"],
            client: "地平线",
            year: "2020",
            img: require("../assets/gyh/dpxzz/ty.png"),
            gallery: [
              require("../assets/gyh/dpxzz/ty.png"),
              require("../assets/gyh/dpxzz/zjbx.png"),
              require("../assets/gyh/dpxzz/zjjs.png"),
              require("../assets/gyh/dpxzz/zjsy.png"),
            ],
            detail:
              "地平线为智能驾驶场景下的视频展示，满足政务场景对安全合规、操作审计与易用性的要求。",
            highlights: [
              "视频展示：全场景全天候视频展示",
              "辅助驾驶系统：城区辅助驾驶系统",
            ],
          }
        ],
        custom: [
          {
            name: '高新区"智慧监管平台"',
            type: "数据中台",
            desc: '商户口罩识别、厨师帽识别、抽烟识别、老鼠识别等AI事件自动派单至商户负责人闭环处置。',
            tags: ["AI识别", "视频分析", "派单闭环"],
            client: "高新区食药监",
            year: "2023",
            img: require("../assets/gaoxin/one.png"),
            gallery: [
              require("../assets/gaoxin/one.png"),
              require("../assets/gaoxin/tow.png"),
              require("../assets/gaoxin/thress.png"),
            ],
            detail:
              '高新区"智慧监管平台"面向商户食品安全监管场景，接入商户配餐区、洗料区、熟食区监控视频，通过AI算法自动识别口罩佩戴、厨师帽、抽烟、老鼠等违规事件，识别结果以派单形式下发商户负责人闭环处置，有效降低人工巡查成本，提升监管响应速度。',
            highlights: [
              "多路AI识别：口罩 / 厨师帽 / 抽烟 / 老鼠 多场景识别",
              "商户分级派单：按区域与商户负责人自动分拨",
              "处置闭环跟踪：整改时效、复核留痕、预警统计",
              "商户端协同：负责人小程序接收 / 上传整改凭证",
            ],
          },
        ],
        app: [
          {
            name: "巡查员现场APP",
            type: "移动应用",
            desc: "巡查员现场拍照、定位签到、任务处置与结果回传的移动端应用。",
            tags: ["移动", "拍照", "定位"],
            client: "市场监管局",
            year: "2023",
            img: img("[PURPOSE]: Project case cover. [DESC]: Smartphone and tablet showing a clean field inspection app interface on a white background, minimal elegant composition, soft shadows, product photography, Airbnb aesthetic, no text"),
          },
          {
            name: "监管指挥端APP",
            type: "管理应用",
            desc: "管理者移动端查看实时态势、审批工单与指挥调度。",
            tags: ["管理", "态势", "调度"],
            client: "高新区综治中心",
            year: "2023",
            img: img("[PURPOSE]: Project case cover. [DESC]: Tablet showing a command and dispatch app interface, map and task cards, cool blue tones, clean desk, product photography, Airbnb aesthetic, no text"),
          },
          {
            name: "海洋监测终端",
            type: "现场终端",
            desc: "海上站点现场数据查看与设备控制的专用终端应用。",
            tags: ["终端", "控制", "海洋"],
            client: "海洋监测站",
            year: "2022",
            img: img("[PURPOSE]: Project case cover. [DESC]: Rugged tablet showing a marine monitoring app interface near sea, cool tones, bright daylight, product photography, Airbnb aesthetic, no text"),
          },
          {
            name: "产线工位终端",
            type: "工位应用",
            desc: "产线工位扫码作业、工序指引与异常上报的专用工位终端。",
            tags: ["工位", "扫码", "工序"],
            client: "某标签包装企业",
            year: "2022",
            img: img("[PURPOSE]: Project case cover. [DESC]: Industrial workstation terminal showing a clean app interface, barcode and step cards, warm neutral tones, factory, product photography, Airbnb aesthetic, no text"),
          },
          {
            name: "养殖户移动端",
            type: "移动应用",
            desc: "养殖户手机查看水质气象、接收预警与远程投喂控制的移动应用。",
            tags: ["移动", "预警", "远程控制"],
            client: "某水产养殖企业",
            year: "2021",
            img: img("[PURPOSE]: Project case cover. [DESC]: Smartphone showing an aquaculture app interface held in hand, pond background, green and blue tones, outdoor soft light, product photography, Airbnb aesthetic, no text"),
          },
          {
            name: "园区访客APP",
            type: "公众应用",
            desc: "访客预约、通行、消费与活动参与的园区公众移动应用。",
            tags: ["公众", "预约", "通行"],
            client: "广州国际生物岛",
            year: "2021",
            img: img("[PURPOSE]: Project case cover. [DESC]: Smartphone showing a campus visitor app interface on a white background, clean colorful cards, minimal elegant composition, soft shadows, product photography, Airbnb aesthetic, no text"),
          },
        ],
      },
    };
  },
  computed: {
    slug() {
      return this.$route.params.slug || "website";
    },
    category() {
      return (
        this.categories.find((c) => c.slug === this.slug) || this.categories[0]
      );
    },
    list() {
      return this.projectMap[this.slug] || [];
    },
  },
  watch: {
    slug() {
      this.$nextTick(() => {
        const grid = document.querySelector(".ph-grid-section");
        const topbar = document.querySelector(".topbar");
        if (grid) {
          const rect = grid.getBoundingClientRect();
          const topbarH = topbar ? topbar.offsetHeight : 0;
          const targetY = rect.top + window.scrollY - topbarH - 30 ;
          window.scrollTo({ top: targetY, behavior: "smooth" });
        }
        this.initReveal();
        this.setMeta();
      });
    },
  },
  mounted() {
    this.setMeta();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("keydown", this.onKeydown);
    this.$nextTick(this.initReveal);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
    if (this.revealObserver) {
      this.revealObserver.disconnect();
      this.revealObserver = null;
    }
  },
  methods: {
    setMeta() {
      setSeo({
        title: this.category.name + "项目案例-广州新水科技有限公司",
        keywords:
          this.category.name +
          "案例," +
          this.category.name +
          "项目,广州新水科技,智慧监管,海洋监测,工业物联",
        description:
          "广州新水科技" +
          this.category.name +
          "案例集：" +
          this.category.desc,
      });
    },
    onScroll() {
      this.scrolled = window.scrollY > 40;
    },
    initReveal() {
      const root = this.$el;
      if (!root) return;
      const els = root.querySelectorAll(".reveal, .reveal-stagger");
      if (!els.length) return;
      if (!("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }
      if (this.revealObserver) this.revealObserver.disconnect();
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              this.revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this.revealObserver.observe(el));
    },
    openDetail(p) {
      if (!p.gallery || !p.gallery.length) return;
      this.activeProject = p;
      document.body.style.overflow = "hidden";
    },
    closeDetail() {
      this.activeProject = null;
      document.body.style.overflow = "";
    },
    previewImg(src) {
      this.activeImg = src;
    },
    closeImg() {
      this.activeImg = "";
    },
    onKeydown(e) {
      if (e.key !== "Escape") return;
      if (this.activeImg) {
        this.closeImg();
      } else if (this.activeProject) {
        this.closeDetail();
      }
    },
  },
};
</script>

<style lang="less" scoped>
#projectList {
  --rausch: #ff385c;
  --rausch-dark: #e00b41;
  --ink: #222222;
  --foggy: #6a6a6a;
  --bebe: #ebebeb;
  --deco: #dddddd;
  --faint: #f7f7f7;
  --white: #ffffff;
  --radius: 18px;
  min-height: 100vh;
  background: var(--white);
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

/* ========== 顶部返回条 ========== */
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid transparent;
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;

  &.is-scrolled {
    background: rgba(255, 255, 255, 0.96);
    border-color: var(--bebe);
    box-shadow: 0 6px 24px rgba(0, 20, 60, 0.06);
  }

  .topbar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    text-decoration: none;

    .back-arrow {
      transition: transform 0.25s;
    }

    &:hover {
      color: var(--rausch);

      .back-arrow {
        transform: translateX(-4px);
      }
    }
  }

  .topbar-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--foggy);
    letter-spacing: 1px;
  }
}

/* ========== Hero ========== */
.ph-hero {
  margin-top: 60px;
  padding: 88px 0 56px;
  background: linear-gradient(180deg, #0c2a66 0%, #114dd9 100%);
  color: #fff;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    right: -120px;
    top: -120px;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: radial-gradient(circle,
        rgba(255, 56, 92, 0.32),
        transparent 65%);
    pointer-events: none;
  }

  &::before {
    content: "";
    position: absolute;
    left: -80px;
    bottom: -160px;
    width: 360px;
    height: 360px;
    border-radius: 50%;
    background: radial-gradient(circle,
        rgba(170, 252, 255, 0.28),
        transparent 65%);
    pointer-events: none;
  }

  .ph-hero-inner {
    position: relative;
    z-index: 2;
  }

  .ph-kicker {
    font-size: 13px;
    letter-spacing: 4px;
    color: rgba(170, 252, 255, 0.9);
    font-weight: 700;
    margin-bottom: 18px;
  }

  .ph-title {
    font-size: 56px;
    font-weight: 800;
    line-height: 1.1;
    margin: 0 0 18px;
  }

  .ph-desc {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.82);
    max-width: 640px;
    margin: 0 0 32px;
  }

  .ph-stat {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 12px 22px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);

    .ph-stat-num {
      font-size: 28px;
      font-weight: 800;
      background: linear-gradient(180deg, #ffffff, #aafcff);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .ph-stat-label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

/* ========== 分类切换 ========== */
.ph-tabs {
  position: sticky;
  top: 60px;
  z-index: 40;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--bebe);

  .ph-tabs-inner {
    display: flex;
    gap: 8px;
    padding: 14px 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .ph-tab {
    flex: 0 0 auto;
    padding: 9px 22px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    color: var(--foggy);
    text-decoration: none;
    background: var(--faint);
    border: 1px solid transparent;
    transition: color 0.2s, background 0.2s, border-color 0.2s, transform 0.2s;

    &:hover {
      color: var(--ink);
      transform: translateY(-1px);
    }

    &.active {
      color: #fff;
      background: linear-gradient(135deg, var(--rausch), var(--rausch-dark));
      border-color: var(--rausch);
      box-shadow: 0 6px 18px rgba(255, 56, 92, 0.28);
    }
  }
}

/* ========== 项目网格 ========== */
.ph-grid-section {
  padding: 56px 0 80px;
  background: var(--faint);
}

.ph-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.ph-card {
  background: var(--white);
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--bebe);
  box-shadow: 0 4px 18px rgba(0, 20, 60, 0.04);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s ease, border-color 0.35s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 22px 48px rgba(0, 20, 60, 0.14);
    border-color: transparent;

    .ph-cover img {
      transform: scale(1.08);
    }

    .ph-cover-mask {
      opacity: 1;
    }

    .ph-tags {
      transform: translateY(0);
      opacity: 1;
    }

    .ph-cta {
      color: var(--rausch);
      gap: 8px;
    }
  }

  .ph-cover {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: var(--faint);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
    }
  }

  .ph-cover-mask {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg,
        transparent 40%,
        rgba(12, 42, 102, 0.55) 100%);
    opacity: 0;
    transition: opacity 0.35s;
    pointer-events: none;
  }

  .ph-tags {
    position: absolute;
    left: 14px;
    bottom: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    transform: translateY(8px);
    opacity: 0;
    transition: transform 0.35s, opacity 0.35s;

    span {
      padding: 3px 10px;
      font-size: 11px;
      color: #fff;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.32);
      backdrop-filter: blur(4px);
    }
  }

  .ph-year {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    border-radius: 999px;
    background: rgba(255, 56, 92, 0.85);
  }

  .ph-body {
    padding: 22px 22px 20px;
  }

  .ph-no {
    font-size: 12px;
    font-weight: 800;
    color: var(--rausch);
    letter-spacing: 1px;
    margin-bottom: 8px;
  }

  .ph-name {
    font-size: 19px;
    font-weight: 700;
    color: var(--ink);
    margin: 0 0 12px;
    line-height: 1.35;
  }

  .ph-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 10px;

    .ph-meta-k {
      color: var(--foggy);
      background: var(--faint);
      padding: 2px 8px;
      border-radius: 4px;
    }

    .ph-meta-v {
      color: var(--ink);
      font-weight: 600;
    }
  }

  .ph-desc {
    font-size: 13px;
    line-height: 1.7;
    color: var(--foggy);
    margin: 0 0 16px;
  }

  .ph-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 14px;
    border-top: 1px solid var(--bebe);
  }

  .ph-client {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;

    .ph-client-k {
      color: var(--foggy);
    }

    .ph-client-v {
      color: var(--ink);
      font-weight: 600;
    }
  }

  .ph-cta {
    font-size: 13px;
    font-weight: 600;
    color: var(--foggy);
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 0.25s, gap 0.25s;
  }
}

.ph-empty {
  text-align: center;
  padding: 80px 0;
  color: var(--foggy);
  font-size: 15px;
}

/* ========== 底部 ========== */
.ph-footer {
  background: var(--ink);
  padding: 28px 0;

  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  .ph-footer-back {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: var(--rausch);
    }
  }

  .ph-icp {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-decoration: none;
  }
}

/* ========== 滚动入场动画 ========== */
.reveal,
.reveal-stagger>* {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1),
    transform 1.1s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.reveal.is-visible,
.reveal-stagger.is-visible>* {
  opacity: 1;
  transform: translateY(0);
}

.reveal-stagger.is-visible>*:nth-child(1) {
  transition-delay: 0s;
}

.reveal-stagger.is-visible>*:nth-child(2) {
  transition-delay: 0.12s;
}

.reveal-stagger.is-visible>*:nth-child(3) {
  transition-delay: 0.24s;
}

.reveal-stagger.is-visible>*:nth-child(4) {
  transition-delay: 0.36s;
}

/* ========== 项目详情弹窗 ========== */
.ph-modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 15, 40, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.ph-modal-box {
  position: relative;
  width: 100%;
  max-width: 960px;
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 22px;
  padding: 40px 36px 32px;
  box-sizing: border-box;
  box-shadow: 0 30px 80px rgba(0, 20, 60, 0.28);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--deco);
    border-radius: 4px;
  }
}

.ph-modal-close {
  position: absolute;
  top: 14px;
  right: 18px;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: var(--faint);
  color: var(--ink);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s, transform 0.2s;

  &:hover {
    background: var(--rausch);
    color: #fff;
    transform: rotate(90deg);
  }
}

.ph-modal-kicker {
  font-size: 12px;
  letter-spacing: 2px;
  font-weight: 700;
  color: var(--rausch);
  margin-bottom: 10px;
}

.ph-modal-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--ink);
  margin: 0 0 14px;
  line-height: 1.3;
}

.ph-modal-desc {
  font-size: 14px;
  line-height: 1.85;
  color: var(--foggy);
  margin: 0;
}

.ph-modal-highlights {
  margin: 22px 0 24px;
  display: grid;
  gap: 12px;
}

.ph-hl {
  position: relative;
  padding-left: 22px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--ink);

  .ph-hl-dot {
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--rausch), var(--rausch-dark));
  }
}

.ph-modal-grid {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  padding-bottom: 6px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.ph-modal-img {
  flex: 0 0 auto;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: zoom-in;
  background: #0c2a66;
  border: 1px solid var(--bebe);
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 26px rgba(0, 20, 60, 0.16);

    .ph-modal-img-tip {
      opacity: 1;
    }
  }

  img {
    display: block;
    height: 240px;
    width: auto;
    max-width: 320px;
    object-fit: contain;
  }

  .ph-modal-img-tip {
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 2px 8px;
    font-size: 11px;
    color: #fff;
    border-radius: 999px;
    background: rgba(0, 15, 40, 0.6);
    opacity: 0;
    transition: opacity 0.25s;
  }
}

.ph-modal-tip {
  text-align: center;
  font-size: 12px;
  color: var(--foggy);
  margin-top: 18px;
}

/* 大图预览 */
.ph-imgview {
  position: fixed;
  inset: 0;
  z-index: 120;
  background: rgba(0, 0, 0, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  box-sizing: border-box;
  cursor: zoom-out;

  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }
}

.ph-imgview-close {
  position: absolute;
  top: 20px;
  right: 24px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--rausch);
  }
}

/* 过渡 */
.ph-fade-enter-active,
.ph-fade-leave-active {
  transition: opacity 0.25s ease;
}

.ph-fade-enter,
.ph-fade-leave-to {
  opacity: 0;
}

/* ========== 响应式 ========== */
@media screen and (max-width: 1024px) {
  .ph-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .ph-hero {
    padding: 64px 0 44px;

    .ph-title {
      font-size: 44px;
    }
  }
}

@media screen and (max-width: 768px) {
  .topbar .topbar-title {
    display: none;
  }

  .ph-hero {
    padding: 48px 0 36px;

    .ph-title {
      font-size: 32px;
    }

    .ph-desc {
      font-size: 14px;
    }

    .ph-stat {
      padding: 9px 16px;

      .ph-stat-num {
        font-size: 22px;
      }
    }
  }

  .ph-tabs {
    top: 60px;

    .ph-tab {
      padding: 8px 16px;
      font-size: 13px;
    }
  }

  .ph-grid-section {
    padding: 36px 0 56px;
  }

  .ph-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .ph-card .ph-body {
    padding: 18px 16px;
  }

  .ph-footer .container {
    flex-direction: column;
    align-items: flex-start;
  }

  .ph-modal {
    padding: 12px;
  }

  .ph-modal-box {
    padding: 28px 18px 22px;
    border-radius: 18px;
  }

  .ph-modal-title {
    font-size: 22px;
  }

  .ph-modal-desc {
    font-size: 13px;
  }

  .ph-modal-grid {
    gap: 10px;
  }

  .ph-modal-img img {
    height: 200px;
    max-width: 260px;
  }

  .ph-hl {
    font-size: 13px;
  }
}
</style>
